// LocationSection.jsx
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

const LocationSection = ({
  form,
  errors,
  countryOptions,
  cityOptions,
  setForm,
  handleInputChange,
  handleMapSearch,
  handleClearPin,
}) => {
  const { t, i18n } = useTranslation();
   // Load Google Maps API and initialize interactive map
  useEffect(() => {
    const loadGoogleMaps = () => {
      if (window.google && window.google.maps) {
        initializeInteractiveMap();
        return;
      }

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${
        import.meta.env.VITE_GOOGLE_MAP_API_KEY ||
        "AIzaSyBFw0Qbyq9zTFTd-tUY6dOWWgUfXrH3g1E"
      }&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        initializeInteractiveMap();
      };
      script.onerror = () => {
        console.error("Failed to load Google Maps API");
      };
      document.head.appendChild(script);
    };

    const initializeInteractiveMap = () => {
      const mapElement = document.getElementById("interactive-map");
      if (!mapElement || !window.google) return;

      // Default center (Amman, Jordan)
      const defaultCenter = { lat: 31.9539, lng: 35.9106 };

      // Use existing coordinates or default
      const center =
        form.latitude && form.longitude
          ? { lat: parseFloat(form.latitude), lng: parseFloat(form.longitude) }
          : defaultCenter;

      const map = new window.google.maps.Map(mapElement, {
        zoom: 15,
        center: center,
        mapTypeId: "roadmap",
        styles: [
          {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }],
          },
        ],
      });

      // Store map reference globally
      window.interactiveMap = map;

      // Function to create/update marker
      const createMarker = (position) => {
        // Remove existing marker
        if (window.interactiveMapMarker) {
          window.interactiveMapMarker.setMap(null);
        }

        // Create new marker
        const marker = new window.google.maps.Marker({
          position: position,
          map: map,
          title: t("clinicSetup.location.markerTitle"),
          draggable: true,
          animation: window.google.maps.Animation.DROP,
        });

        // Add drag listener
        marker.addListener("dragend", (event) => {
          const lat = event.latLng.lat();
          const lng = event.latLng.lng();

          setForm((prev) => ({
            ...prev,
            latitude: lat.toString(),
            longitude: lng.toString(),
            map_coordinates: { lat: lat, lng: lng },
          }));
        });

        // Store marker reference globally
        window.interactiveMapMarker = marker;
        return marker;
      };

      // Add initial marker if coordinates exist
      if (form.latitude && form.longitude) {
        createMarker(center);
      }

      // Add click listener to place marker
      map.addListener("click", (event) => {

        const lat = event.latLng.lat();
        const lng = event.latLng.lng();

        // Update form state immediately
        setForm((prev) => ({
          ...prev,
          latitude: lat.toString(),
          longitude: lng.toString(),
          map_coordinates: { lat: lat, lng: lng },
        }));

        // Create marker at clicked position
        createMarker(event.latLng);

      });
    };

    // Load maps after a short delay to ensure DOM is ready
    const timer = setTimeout(loadGoogleMaps, 1000);

    return () => {
      clearTimeout(timer);
      if (window.interactiveMap) {
        window.interactiveMap = null;
      }
    };
  }, [form.latitude, form.longitude, setForm]);

  return (
    <div
      style={{
        padding: "0",
        width: "100%",
        maxWidth: "960px",
        margin: "0 auto",
      }}
      dir={i18n.dir()}
      lang={i18n.language}
    >
      {/* Address Information Card */}
      <div style={{
        background: "#fff",
        padding: "24px",
        marginBottom: "24px"
      }}>
        <h3 style={{ 
          fontSize: "18px", 
          fontWeight: "600", 
          color: "#1f2937",
          marginBottom: "20px"
        }}>
          {t("clinicSetup.location.title")}
        </h3>
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
            <div>
              <label style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "8px"
              }}>
                {t("clinicSetup.location.fields.country.label")} <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <select
                name="country"
                value={form.country}
              onChange={handleInputChange}
              style={{
                width: "100%",
                maxWidth: "450px",
                padding: "12px 16px",
                fontSize: "15px",
                border: errors.country ? "2px solid #ef4444" : "2px solid #e5e7eb",
                borderRadius: "10px",
                outline: "none",
                cursor: "pointer",
                background: "#fff",
                boxSizing: "border-box",
                transition: "all 0.2s ease",
                appearance: "none",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23374151' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 16px center",
                  paddingRight: "40px"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3b82f6";
                  e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = errors.country ? "#ef4444" : "#e5e7eb";
                  e.target.style.boxShadow = "none";
                }}
              >
                <option value="" disabled>
                  {t("clinicSetup.location.fields.country.placeholder")}
                </option>
                {countryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.country && (
                <div style={{ color: "#ef4444", fontSize: "13px", marginTop: "6px" }}>
                  {errors.country}
                </div>
              )}
            </div>
            <div>
              <label style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "8px"
              }}>
                {t("clinicSetup.location.fields.city.label")} <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <select
                name="city"
                value={form.city}
                onChange={handleInputChange}
                style={{
                  width: "100%",
                  maxWidth: "450px",
                  padding: "12px 16px",
                  fontSize: "15px",
                  border: errors.city ? "2px solid #ef4444" : "2px solid #e5e7eb",
                  borderRadius: "10px",
                  outline: "none",
                  cursor: "pointer",
                  background: "#fff",
                  boxSizing: "border-box",
                  transition: "all 0.2s ease",
                  appearance: "none",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23374151' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 16px center",
                  paddingRight: "40px"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3b82f6";
                  e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = errors.city ? "#ef4444" : "#e5e7eb";
                  e.target.style.boxShadow = "none";
                }}
              >
                <option value="" disabled>
                  {t("clinicSetup.location.fields.city.placeholder")}
                </option>
                {cityOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.city && (
                <div style={{ color: "#ef4444", fontSize: "13px", marginTop: "6px" }}>
                  {errors.city}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
            <div>
              <label style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "8px"
              }}>
                {t("clinicSetup.location.fields.street.label")} <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                type="text"
                name="street"
                value={form.street}
                onChange={handleInputChange}
              placeholder={t("clinicSetup.location.fields.street.placeholder")}
              style={{
                width: "100%",
                maxWidth: "450px",
                padding: "12px 16px",
                  fontSize: "15px",
                  border: errors.street ? "2px solid #ef4444" : "2px solid #e5e7eb",
                  borderRadius: "10px",
                  outline: "none",
                  transition: "all 0.2s ease",
                  background: "#fff",
                  boxSizing: "border-box"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3b82f6";
                  e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = errors.street ? "#ef4444" : "#e5e7eb";
                  e.target.style.boxShadow = "none";
                }}
              />
              {errors.street && (
                <div style={{ color: "#ef4444", fontSize: "13px", marginTop: "6px" }}>
                  {errors.street}
                </div>
              )}
            </div>
            <div>
              <label style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "8px"
              }}>
                {t("clinicSetup.location.fields.buildingNumber.label")} <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                type="text"
                name="buildingNumber"
                value={form.buildingNumber}
                onChange={handleInputChange}
              placeholder={t("clinicSetup.location.fields.buildingNumber.placeholder")}
              style={{
                width: "100%",
                maxWidth: "450px",
                padding: "12px 16px",
                  fontSize: "15px",
                  border: errors.buildingNumber ? "2px solid #ef4444" : "2px solid #e5e7eb",
                  borderRadius: "10px",
                  outline: "none",
                  transition: "all 0.2s ease",
                  background: "#fff",
                  boxSizing: "border-box"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3b82f6";
                  e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = errors.buildingNumber ? "#ef4444" : "#e5e7eb";
                  e.target.style.boxShadow = "none";
                }}
              />
              {errors.buildingNumber && (
                <div style={{ color: "#ef4444", fontSize: "13px", marginTop: "6px" }}>
                  {errors.buildingNumber}
                </div>
              )}
            </div>
          </div>
          <div>
            <label style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "500",
              color: "#374151",
              marginBottom: "8px"
            }}>
              {t("clinicSetup.location.fields.floorNumber.label")}
            </label>
            <input
              type="text"
              name="floorNumber"
              value={form.floorNumber}
              onChange={handleInputChange}
              placeholder={t("clinicSetup.location.fields.floorNumber.placeholder")}
              style={{
                width: "100%",
                maxWidth: "450px",
                padding: "12px 16px",
                fontSize: "15px",
                border: errors.floorNumber ? "2px solid #ef4444" : "2px solid #e5e7eb",
                borderRadius: "10px",
                outline: "none",
                transition: "all 0.2s ease",
                background: "#fff",
                boxSizing: "border-box"
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#3b82f6";
                e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = errors.floorNumber ? "#ef4444" : "#e5e7eb";
                e.target.style.boxShadow = "none";
              }}
            />
            {errors.floorNumber && (
              <div style={{ color: "#ef4444", fontSize: "13px", marginTop: "6px" }}>
                {errors.floorNumber}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Map Section Card */}
      <div style={{
        background: "#fff",
        padding: "24px"
      }}>
        <h3 style={{ 
          fontSize: "18px", 
          fontWeight: "600", 
          color: "#1f2937",
          marginBottom: "20px"
        }}>
          {t("clinicSetup.location.map.title")}
        </h3>
        <div style={{ marginBottom: "16px" }}>
          <label style={{
            display: "block",
            fontSize: "14px",
            fontWeight: "500",
            color: "#374151",
            marginBottom: "8px"
          }}>
            {t("clinicSetup.location.map.searchLabel")}
          </label>
          <div style={{
            display: "flex",
            gap: "12px"
          }}>
            <input
              type="text"
              name="mapSearchQuery"
              value={form.mapSearchQuery}
              onChange={handleInputChange}
              placeholder={t("clinicSetup.location.map.searchPlaceholder")}
              style={{
                flex: 1,
                padding: "12px 16px",
                fontSize: "15px",
                border: "2px solid #e5e7eb",
                borderRadius: "10px",
                outline: "none",
                transition: "all 0.2s ease",
                background: "#fff",
                boxSizing: "border-box"
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#3b82f6";
                e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#e5e7eb";
                e.target.style.boxShadow = "none";
              }}
            />
            <button
              type="button"
              onClick={handleMapSearch}
              style={{
                padding: "12px 24px",
                fontSize: "15px",
                fontWeight: "500",
                color: "#fff",
                background: "#3b82f6",
                border: "none",
                borderRadius: "10px",
                cursor: "pointer",
                transition: "all 0.2s ease"
              }}
              onMouseEnter={(e) => (e.target.style.background = "#2563eb")}
              onMouseLeave={(e) => (e.target.style.background = "#3b82f6")}
            >
              {t("clinicSetup.buttons.search")}
            </button>
            <button
              type="button"
              onClick={handleClearPin}
              style={{
                padding: "12px 24px",
                fontSize: "15px",
                fontWeight: "500",
                color: "#6b7280",
                background: "#f3f4f6",
                border: "none",
                borderRadius: "10px",
                cursor: "pointer",
                transition: "all 0.2s ease"
              }}
              onMouseEnter={(e) => (e.target.style.background = "#e5e7eb")}
              onMouseLeave={(e) => (e.target.style.background = "#f3f4f6")}
            >
              {t("clinicSetup.buttons.clear")}
            </button>
          </div>
        </div>
        <div
          style={{
            width: "100%",
            height: "450px",
            borderRadius: "12px",
            overflow: "hidden",
            border: "2px solid #e5e7eb",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
          }}
        >
          <div
            id="interactive-map"
            style={{ width: "100%", height: "100%" }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default LocationSection;
